import { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import type { Expense, CreateExpenseInput, RecurrenceType } from '../hooks/useExpenses';
import type { ExpenseCategory } from '../hooks/useExpenseCategories';

interface ExpenseFormDialogProps {
    open: boolean;
    onClose: () => void;
    expense: Expense | null;
    categories: ExpenseCategory[];
    currency: string;
    onSave: (input: CreateExpenseInput) => Promise<Expense | null>;
    onUpdate: (expenseId: number, input: Partial<CreateExpenseInput>) => Promise<Expense | null>;
    onDelete: (expenseId: number) => Promise<boolean>;
}

const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        '& input, & textarea': {
            backgroundColor: '#0f0f23',
        },
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.875rem',
        letterSpacing: '0.05em',
    },
};

const selectStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: '#0f0f23',
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.875rem',
        letterSpacing: '0.05em',
    },
};

const ExpenseFormDialog = ({
    open,
    onClose,
    expense,
    categories,
    currency,
    onSave,
    onUpdate,
    onDelete,
}: ExpenseFormDialogProps) => {
    const isEditing = expense !== null;
    const canDelete = isEditing && !expense.isAutoGenerated;

    const [categoryId, setCategoryId] = useState<number | ''>('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('monthly');
    const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            if (expense) {
                setCategoryId(expense.categoryId);
                setAmount(expense.amount.toString());
                setDescription(expense.description || '');
                setDate(expense.date);
                setIsRecurring(expense.isRecurring);
                setRecurrenceType(expense.recurrenceType || 'monthly');
                setRecurrenceEndDate(expense.recurrenceEndDate || '');
            } else {
                setCategoryId('');
                setAmount('');
                setDescription('');
                setDate(new Date().toISOString().split('T')[0]);
                setIsRecurring(false);
                setRecurrenceType('monthly');
                setRecurrenceEndDate('');
            }
            setError(null);
        }
    }, [open, expense]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (categoryId === '' || !amount || !date) {
            setError('Please fill in all required fields');
            return;
        }

        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount < 0) {
            setError('Amount must be a positive number');
            return;
        }

        setLoading(true);
        setError(null);

        const input: CreateExpenseInput = {
            categoryId: categoryId as number,
            amount: parsedAmount,
            description: description || undefined,
            date,
            isRecurring,
            recurrenceType: isRecurring ? recurrenceType : undefined,
            recurrenceEndDate: isRecurring && recurrenceEndDate ? recurrenceEndDate : undefined,
        };

        try {
            let result;
            if (isEditing) {
                result = await onUpdate(expense.id, input);
            } else {
                result = await onSave(input);
            }

            if (result) {
                onClose();
            } else {
                setError('Failed to save expense');
            }
        } catch {
            setError('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!expense) return;

        setLoading(true);
        setError(null);

        try {
            const success = await onDelete(expense.id);
            if (success) {
                onClose();
            } else {
                setError('Failed to delete expense');
            }
        } catch {
            setError('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2.5,
                    }}
                >
                    {isEditing ? 'Edit Expense' : 'Add Expense'}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                    <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                        {error && (
                            <Alert
                                severity="error"
                                onClose={() => setError(null)}
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': { color: '#ef4444' },
                                }}
                            >
                                {error}
                            </Alert>
                        )}

                        {expense?.isAutoGenerated && (
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': { color: '#6366f1' },
                                }}
                            >
                                This expense was auto-generated and cannot be edited.
                            </Alert>
                        )}

                        <TextField
                            label="Date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                            disabled={loading || expense?.isAutoGenerated}
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={textFieldStyles}
                        />

                        <FormControl fullWidth sx={selectStyles}>
                            <InputLabel id="category-label">Category</InputLabel>
                            <Select
                                labelId="category-label"
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value as number)}
                                label="Category"
                                required
                                disabled={loading || expense?.isAutoGenerated}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                        {cat.isSystem && (
                                            <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.75rem' }}>
                                                (System)
                                            </span>
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label={`Amount (${currency})`}
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            disabled={loading || expense?.isAutoGenerated}
                            fullWidth
                            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                            sx={textFieldStyles}
                        />

                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={loading || expense?.isAutoGenerated}
                            fullWidth
                            multiline
                            rows={2}
                            sx={textFieldStyles}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isRecurring}
                                    onChange={(e) => setIsRecurring(e.target.checked)}
                                    disabled={loading || expense?.isAutoGenerated}
                                    sx={{
                                        color: '#374151',
                                        '&.Mui-checked': {
                                            color: '#6366f1',
                                        },
                                    }}
                                />
                            }
                            label="Recurring Expense"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.05em',
                                },
                            }}
                        />

                        {isRecurring && (
                            <>
                                <FormControl fullWidth sx={selectStyles}>
                                    <InputLabel id="recurrence-type-label">Recurrence Type</InputLabel>
                                    <Select
                                        labelId="recurrence-type-label"
                                        value={recurrenceType}
                                        onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
                                        label="Recurrence Type"
                                        disabled={loading || expense?.isAutoGenerated}
                                    >
                                        <MenuItem value="monthly">Monthly</MenuItem>
                                        <MenuItem value="quarterly">Quarterly</MenuItem>
                                        <MenuItem value="yearly">Yearly</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="End Date (Optional)"
                                    type="date"
                                    value={recurrenceEndDate}
                                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                                    disabled={loading || expense?.isAutoGenerated}
                                    fullWidth
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={textFieldStyles}
                                />
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                        justifyContent: canDelete ? 'space-between' : 'flex-end',
                    }}
                >
                    {canDelete && (
                        <Button
                            onClick={handleDelete}
                            disabled={loading}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: 'error.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                },
                            }}
                        >
                            Delete
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button
                            onClick={onClose}
                            disabled={loading}
                            variant="outlined"
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    backgroundColor: 'rgba(55, 65, 81, 0.1)',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        {!expense?.isAutoGenerated && (
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                sx={{
                                    borderRadius: 0,
                                    border: '2px solid #6366f1',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 3,
                                    py: 1,
                                }}
                            >
                                {isEditing ? 'Update' : 'Create'}
                            </Button>
                        )}
                    </div>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ExpenseFormDialog;
